import { IPromise } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { IUIError } from "@gtmhub/error-handling";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { FeedActions } from "@gtmhub/feed/redux/feed-actions";
import { IFeedEntry, IFeedEntryComment, toFeedEntryComment } from "@gtmhub/feed/models";
import { IComment } from "@gtmhub/comments";
import { IFeedStoreState } from "@gtmhub/feed/redux/feed-reducer";
import { getTargetTypeForComments } from "@gtmhub/feed/utils";
import { IStateInit } from "@gtmhub/core/routing";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";

export interface IFeedCommentsScope extends IModalScope {
  params: IFeedCommentsStateParams;
  assigneesIdMap: IAssigneeIdMap;
  indicators: {
    getItem: boolean;
  };
  feedEntry: IFeedEntry;
  feedFetched: boolean;
  reduxItemsError: IUIError;
  getTargetTypeForComments(string): string;
  editedCommentId: string;
  handleEditorOpen(commentId: string): void;
}

interface IFeedCommentsStateParams {
  targetId: string;
}

const bindStateToScope = (state: IAssigneesStoreState & IFeedStoreState) => {
  if (state.feed.selectedFeedEntry) {
    state.feed.selectedFeedEntry.displayed = true;
  }

  return {
    assigneesIdMap: state.assignees.map,
    feedEntry: state.feed.selectedFeedEntry,
    feedFetched: state.feed.isFetched,
    reduxItemsError: state.assignees.error || state.feed.error,
  };
};

export class FeedCommentsThreadCtrl implements IStateInit {
  public static $inject = ["$scope", "$stateParams", "$ngRedux", "AssigneeActions", "FeedActions"];

  constructor(
    private $scope: IFeedCommentsScope & IExtendedRootScopeService,
    private $stateParams: IFeedCommentsStateParams,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions,
    private feedActions: FeedActions
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.$scope.indicators = {
      getItem: false,
    };
    this.$scope.params = { ...this.$stateParams };

    this.$scope.$on("commentPosted", (event, comment) => this.onCommentPosted(comment));
    this.$scope.$on("commentEdited", (event, comment) => this.onCommentEdited(comment));
    this.$scope.$on("commentDeleted", (event, comment) => this.onCommentDeleted(comment));

    this.$scope.getTargetTypeForComments = getTargetTypeForComments;

    this.$scope.handleEditorOpen = (commentId: string) => (this.$scope.editedCommentId = commentId);

    this.$scope.$on("$destroy", () => {
      unsubscribe();
      this.$ngRedux.dispatch(this.feedActions.clearSelectFeedEntry());
    });
  }

  public stateInit(): IPromise<unknown> {
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.feedFetched,
        () => this.$scope.reduxItemsError
      )
      .then(() => {
        this.$ngRedux.dispatch(this.feedActions.selectFeedEntryById(this.$scope.params.targetId));
      });
  }

  private onCommentEdited = (comment: IComment): void => {
    this.$scope.editedCommentId = null;
    const oldComment = this.$scope.feedEntry.comments.find((listComment) => listComment.targetId === comment.id);
    if (oldComment) {
      // feed comments are duplicated in the respective feed entry document with a new ID
      // so don't copy the entire object with id, targetId, etc. - take only the updated content values
      oldComment.text = comment.text;
      oldComment.mentioned = comment.mentioned;
      oldComment.gif = comment.gif;
      oldComment.editedBy = comment.editedBy;
    }
  };

  private onCommentDeleted = (comment: IFeedEntryComment): void => {
    this.$scope.feedEntry.comments = this.$scope.feedEntry.comments.filter((listComment) => listComment.id !== comment.id);
  };

  private onCommentPosted = (comment: IComment): void => {
    this.$scope.editedCommentId = null;
    this.$scope.feedEntry.comments = [...this.$scope.feedEntry.comments, toFeedEntryComment(comment)];
  };
}
